import type { FillAction, ActionResult } from '../types/actions';
import { waitForElement, scrollIntoView, isElementVisible, isElementEnabled } from '../utils/dom-utils';

export async function executeFillAction(action: FillAction): Promise<ActionResult> {
  const startTime = Date.now();

  try {
    // Find element
    let element = document.querySelector(action.selector);

    // Wait for element if specified
    if (!element && action.waitForElement) {
      element = await waitForElement(action.selector, action.waitForElement);
    }

    if (!element) {
      return {
        success: false,
        action,
        error: `Element not found: ${action.selector}`,
        timestamp: Date.now(),
        duration: Date.now() - startTime,
        elementFound: false
      };
    }

    // Check if element is a fillable element
    if (!(element instanceof HTMLInputElement ||
          element instanceof HTMLTextAreaElement ||
          element instanceof HTMLSelectElement)) {
      return {
        success: false,
        action,
        error: `Element is not fillable: ${action.selector}`,
        timestamp: Date.now(),
        duration: Date.now() - startTime,
        elementFound: true
      };
    }

    // Check if element is visible
    if (!isElementVisible(element)) {
      const style = window.getComputedStyle(element);
      const rect = element.getBoundingClientRect();
      const reasons = [];

      if (style.display === 'none') reasons.push('display: none');
      if (style.visibility === 'hidden') reasons.push('visibility: hidden');
      if (style.opacity === '0') reasons.push('opacity: 0');
      if (rect.width === 0 || rect.height === 0) reasons.push(`no dimensions (${rect.width}x${rect.height})`);

      return {
        success: false,
        action,
        error: `Element not visible: ${action.selector}. Reason: ${reasons.join(', ') || 'hidden by parent or CSS'}`,
        timestamp: Date.now(),
        duration: Date.now() - startTime,
        elementFound: true
      };
    }

    // Check if element is enabled
    if (!isElementEnabled(element)) {
      return {
        success: false,
        action,
        error: `Element is disabled: ${action.selector}. Element has disabled='true' attribute.`,
        timestamp: Date.now(),
        duration: Date.now() - startTime,
        elementFound: true
      };
    }

    // Scroll into view if specified
    if (action.scrollIntoView !== false) {
      scrollIntoView(element);
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    // Focus the element
    element.focus();

    const valueToSet = String(action.value);

    // Handle different element types
    if (element instanceof HTMLSelectElement) {
      // Special handling for select elements

      // Clear existing selection if specified
      if (action.clear !== false) {
        element.selectedIndex = -1;
      }

      // Try to find and select the option by value
      let optionFound = false;

      for (let i = 0; i < element.options.length; i++) {
        const option = element.options[i];
        // Match by value, text content, or index
        if (option!.value === valueToSet ||
            option!.text === valueToSet ||
            i === parseInt(valueToSet, 10)) {
          element.selectedIndex = i;
          option!.selected = true;
          optionFound = true;
          break;
        }
      }

      if (!optionFound) {
        return {
          success: false,
          action,
          error: `Option not found in select: "${valueToSet}". Available options: ${Array.from(element.options).map(o => `"${o.value}" (${o.text})`).join(', ')}`,
          timestamp: Date.now(),
          duration: Date.now() - startTime,
          elementFound: true
        };
      }

      // Set value property as well
      element.value = element.options[element.selectedIndex]!.value;

    } else if (element instanceof HTMLInputElement && element.type === 'checkbox') {
      // Special handling for checkbox elements
      const shouldCheck = valueToSet.toLowerCase() === 'true' ||
                         valueToSet === '1' ||
                         valueToSet.toLowerCase() === 'yes' ||
                         valueToSet.toLowerCase() === 'on' ||
                         valueToSet.toLowerCase() === 'checked';

      element.checked = shouldCheck;

    } else if (element instanceof HTMLInputElement && element.type === 'radio') {
      // Special handling for radio button elements
      const shouldCheck = valueToSet.toLowerCase() === 'true' ||
                         valueToSet === '1' ||
                         valueToSet.toLowerCase() === 'yes' ||
                         valueToSet.toLowerCase() === 'on' ||
                         valueToSet.toLowerCase() === 'checked';

      if (shouldCheck) {
        element.checked = true;
      }

    } else if (element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement) {
      // Handle regular input and textarea elements

      // Clear existing value if specified
      if (action.clear !== false) {
        element.value = '';
      }

      // Set the value
      element.value = valueToSet;
    }

    // Trigger input event
    const inputEvent = new Event('input', {
      bubbles: true,
      cancelable: true
    });
    element.dispatchEvent(inputEvent);

    // Trigger change event (important for select elements)
    const changeEvent = new Event('change', {
      bubbles: true,
      cancelable: true
    });
    element.dispatchEvent(changeEvent);

    // Get validation state
    const isValid = element.checkValidity ? element.checkValidity() : true;
    const validationMessage = element.validationMessage || undefined;

    return {
      success: true,
      action,
      timestamp: Date.now(),
      duration: Date.now() - startTime,
      elementFound: true,
      data: {
        fieldType: element.tagName.toLowerCase(),
        inputType: element instanceof HTMLInputElement ? element.type : undefined,
        newValue: element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement || element instanceof HTMLSelectElement
          ? element.value
          : action.value,
        isValid,
        validationMessage
      }
    };
  } catch (error) {
    return {
      success: false,
      action,
      error: error instanceof Error ? error.message : String(error),
      timestamp: Date.now(),
      duration: Date.now() - startTime
    };
  }
}
